// Next SEO
import { BreadcrumbJsonLd, DefaultSeo } from 'next-seo';
import { REEBELO_DOMAINS, REEBELO_STORE_TO_LOCALE } from '@lambda/reebelo';
import { useRouter } from 'next/router';
import { handleFreeReturn } from '@lambda/commons/helpers';
import settings from '@/settings';
import i18n from '@/settings/i18n';
import { useCollectionPageContext } from './hooks/useCollectionPageContext';
import { generateCategoryPageUrl } from './hooks/helpers';
import { getShortenCountryName } from '@/lib/use-origin-country';
import { CATEGORY_SLOT, resolveRefurbishedTitle } from './refurbishedTitle';
import { SITE_NAME } from '@/lib/site-name';
import { fitPageTitle } from '@/lib/page-title';

const t = i18n.init('collections');
const tCategory = i18n.init('categories');

const PATH = `https://${REEBELO_DOMAINS[settings.store]}`;

const Metadata = () => {
  const {
    collectionName: rawCollectionName,
    breadcrumb,
    products,
    type,
  } = useCollectionPageContext();
  const router = useRouter();

  // The meta title/description interpolate the category name into localized
  // copy, so translate it here — otherwise FR/DE meta shows the English name
  // (e.g. "Laptops reconditionnés" instead of "Ordinateurs portables …").
  // Non-category values (brand handles) have no translation and pass through.
  const collectionName = tCategory(`${rawCollectionName}`);

  const isBrandCollectionPage = router.pathname.includes('/collections/brand/');

  let title = '';
  let description = '';

  const freeReturnDays = handleFreeReturn(
    settings.store,
    settings.free_return_days,
  );
  const country = getShortenCountryName();

  if (isBrandCollectionPage) {
    title = t`Best Deals for Refurbished & Unlocked ${{
      collectionName,
    }} | Reebelo ${{ country }}`;
    description = t`Get the best value & price for all ${{
      collectionName,
    }} models now up to 70% off. Shop with peace of mind & guaranteed satisfaction with up to 12 months warranty & ${{
      freeReturnDays,
    }}-day returns`;
  } else if (type === 'MS_CATALOG_DEALS') {
    title =
      settings.store === 'reebelo-us'
        ? t`Explore Hot Deals on Refurbished Tech | Reebelo USA`
        : t`Buy Hot Deals on Refurbished Tech | Reebelo ${{ country }}`;
    description =
      settings.store === 'reebelo-us'
        ? t`Reebelo offers amazing deals on certified, 100% functional refurbished tech. Enjoy your favorite sustainable devices up to 70% off with up to 12 months warranty.`
        : t`Looking for hot tech deals? Shop a wide variety of refurbished phones, laptops, tablets, and more. Up to 70% off with free shipping and ${{
            freeReturnDays,
          }}-day returns.`;
  } else {
    // Agree "reconditionné(e)s … certifié(e)s" with the category, then append
    // the invariant "| Reebelo <country>" suffix in code (keeps the per-category
    // override key brace-free). rawCollectionName is the locale-file key;
    // collectionName is its localized form spliced back in.
    const refurbishedTitle = resolveRefurbishedTitle(
      t,
      'Premium & Certified Refurbished',
      rawCollectionName,
      t`Premium & Certified Refurbished ${{ category: CATEGORY_SLOT }}`,
    )
      .split(CATEGORY_SLOT)
      .join(collectionName);

    title = `${refurbishedTitle} | Reebelo ${country}`;
    description = t`Make your ${{
      collectionName,
    }} upgrade sustainable with Reebelo. Shop a wide selection of iPhones, Samsung Galaxy, Google Pixel, and more. Up to 70% off & ${{
      freeReturnDays,
    }}-day returns`;
  }

  title = fitPageTitle(title);

  const streamlinedBreadcrumb = breadcrumb
    ? [
        { name: 'Home', link: PATH },
        ...breadcrumb.map((c) => {
          const link = `${PATH}${generateCategoryPageUrl(c.name, c.pk)}`;

          return { name: c.name, link };
        }),
      ].map((c, i) => ({
        // Localize the display name so the breadcrumb rich result matches the
        // (localized) page and visible breadcrumb. URLs above are still built
        // from the English name/slug.
        name: tCategory(`${c.name}`),
        item: c.link,
        position: i,
      }))
    : [];

  return (
    <>
      {/* Default SEO */}
      <DefaultSeo
        norobots
        // 🔹 Basic Page Info
        title={title}
        defaultTitle={t`${{ collectionName }} Collection | Reebelo ${{
          country,
        }}`}
        description={description}
        // 🔹 Open Graph
        openGraph={{
          type: 'website',
          locale: REEBELO_STORE_TO_LOCALE[settings.store].replace('-', '_'), // Open Graph uses underscore for locale
          // `asPath` already carries its leading slash; the extra one here
          // produced `og:url`s like `https://reebelo.com//collections/...`,
          // which do not match the page's canonical.
          url: `${PATH}${router.asPath.split('?')[0] ?? ''}`,
          siteName: SITE_NAME,
          title,
          description,
          images: products.slice(0, 5).flatMap((product) =>
            product.images?.length
              ? product.images.map((img) => ({
                  url: img.url,
                  width: 1200,
                  height: 630,
                  alt: 'Default Open Graph Image',
                  type: 'image/jpeg',
                }))
              : [],
          ),
        }}
        // 🔹 Additional Tags
        additionalMetaTags={[{ name: 'publisher', content: 'Reebelo' }]}
      />
      {/* Breadcrumb JSON-LD is structured data that shows a page’s position within your site’s hierarchy 
      (e.g., Home > Phones > iPhone 13). It helps search engines display breadcrumb navigation 
      in search results instead of long URLs. */}
      {streamlinedBreadcrumb.length > 0 && (
        <BreadcrumbJsonLd itemListElements={streamlinedBreadcrumb} />
      )}
    </>
  );
};

export default Metadata;
